import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { RowDefinition } from "./RowDefinition";
import { IndexType } from "./IndexType";
import { BaseTableDefinitionFactory } from "./BaseTableDefinitionFactory";
import { COLUMN_PRIVATIZATION, COLUMN_SELECTION } from "./TableDefinitionFactory.constants";
import { ColumnDefinition } from "./ColumnDefinition";
import { TableDefinition, BfxColDef } from "./TableDefinition";
/***************************************************** */
// ADAPTER imports
// Intervention blotter adapters
/* import { SelectionStatusAdapter } from '../adapter/interventionBlotter/SelectionStatusAdapter';
import { DateAdapter } from '../adapter/DateAdapter';
// Intervention - Root Adapters
import { EventIdAdapter } from '../adapter/interventionBlotter/EventIdAdapter';
import { InstrumentAdapter } from '../adapter/interventionBlotter/InstrumentAdapter';
import { EventTypeAdapter } from "../adapter/interventionBlotter/EventTypeAdapter";
import { TraderGroupNameAdapter } from '../adapter/common/TraderGroupNameAdapter';
import { SalesGroupNameAdapter } from '../adapter/common/SalesGroupNameAdapter';
import { DIStatusAdapter } from '../adapter/interventionBlotter/DIStatusAdapter';
import { DIReasonAdapter } from '../adapter/interventionBlotter/DIReasonAdapter';
import { NullAdapter } from '../adapter/NullAdapter';
import { UserNameAdapter } from '../adapter/common/UserNameAdapter';
// Intervention - Order Adapters
import { InterventionOrderNumericAdapter } from "../adapter/interventionBlotter/InterventionOrderNumericAdapter";
import { OrderClientCodeAdapter } from "../adapter/interventionBlotter/OrderClientCodeAdapter";
import { OrderClientNameAdapter } from '../adapter/interventionBlotter/OrderClientNameAdapter';
import { OrderProductAdapter } from "../adapter/interventionBlotter/OrderProductAdapter";
import { OrderClOrderIdAdapter } from "../adapter/interventionBlotter/OrderClOrderIdAdapter";
import { OrderChannelAdapter } from "../adapter/interventionBlotter/OrderChannelAdapter";
import { OrderPrivatizationAdapter } from "../adapter/interventionBlotter/OrderPrivatizationAdapter"; */
// AssetClassAdapter is used for fields missing in the DealImpl Model/API
import { AssetClassAdapter } from "../adapter/common/AssetClassAdapter";
import { ColumnFilterType } from "./ColumnFilterType";
import { BlotterType, AssetClass } from "shared/cubiq-model-multi-asset";
import { CcyLookupService } from "../lookup/model/impl/CcyLookupService";
import { CcyPairLookupService } from "../lookup/model/impl/CcyPairLookupService";
import { ClientLookupService } from "../lookup/model/impl/ClientLookupService";
import { AccountLookupService } from "../lookup/model/impl/AccountLookupService";
import { TraderGroupLookupService } from "../lookup/model/impl/TraderGroupLookupService";
import { SalesGroupLookupService } from "../lookup/model/impl/SalesGroupLookupService";
import { UserLookupService } from "../lookup/model/impl/UserLookupService";
import { SystemConfigPromiseService } from "shared/auth-services";
import { ProfilePromiseActionService } from "../profile/impl/ProfilePromiseActionService";

import { TranslateService } from "@ngx-translate/core";
import { forwardRef, Inject, Injectable, Optional } from "@angular/core";

import {
  COLUMN_INSTRUMENT,
  COLUMN_DESK_ID,
  COLUMN_ORDER_PRODUCT,
} from "./OrdersTableDefinitionFactory.constants";
import {
  COLUMN_TRADE_INSTRUMENT,
  COLUMN_TRADE_DESK_ID,
  COLUMN_TRADE_ORDER_PRODUCT,
} from "./TradeTableDefinitionFactory.constants";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { INTERVENTION_TABLE_DEFINITION_VER } from "./TableDefinitionFactory.constants";
import { DIStatusHelper } from "./orders/DIStatusHelper";
import { DIReasonHelper } from "./orders/DIReasonHelper";
import { OrderTransactionHelper } from "./orders/OrderTransactionHelper";
import { UserNameHelper } from "./orders/UserNameHelper";
import { COLUMN_ORDER_FIXING_SOURCE_CODE_DATA } from "./BmarkClientOrdersTableDefinitionFactory.constants";
import { StatusIconsHelper, MODIFY_LOCK_IGNORE_STATES } from "./orders/StatusIconsHelper";
import { DealtPricingService } from "../lookup/pricing/impl/DealtPricingService";
import { BfxConfigService } from "shared/auth-services/system-config/bfx-config.service";
import { BfxConfigIDRefHelper } from "../../helper-services/bfx-config-id-ref-helper";
import { DIFilterReason } from "./DIFilterReason";
import {
  COLUMN_INTER_INSTRUMENT,
  COLUMN_INTER_ORDER_PRODUCT,
} from "./InterventionTableDefinitionFactory.constants";
import { ProfileSettingKeys } from "shared/cubiq-services/model/core/ProfileSettingKeys";
import { TimezonePromiseLookupService } from "shared/cubiq-services/lookup/model/impl/TimezonePromiseLookupService";
import { ValueFormatterParams } from "ag-grid-community/main";
import { Observable, Subject, Subscription } from "rxjs";

const DEFAULT_CELL_CLASS: string = "ag-cell-default";
/** Type for storing Fixing Date value used for Order, Fwd Roll, and Pricing */
type FixingValue = {
  order: CubiqTS.OrderData;
  fixingMoment: moment.Moment;
  fixingTime: number;
  code: string;
};

@Injectable()
export class InterventionTableDefinitionFactory extends BaseTableDefinitionFactory {
  protected defaultSortModel: any = [{ colId: "timestamp", sort: "desc" }];
  protected diStatusHelper: DIStatusHelper;
  protected transactionHelper: OrderTransactionHelper;
  protected diReasonHelper: DIReasonHelper;
  protected userNameHelper: UserNameHelper;
  protected Helper: DIReasonHelper;
  protected myStatusIconHelper: StatusIconsHelper;

  private permissionsInitialized: boolean = false;
  private benchmarksAreEnabled: boolean;

  protected enabledOBO: boolean = false;

  private bmDisplayTZ: CubiqTS.TimeZone;
  private settingSubscription: Subscription;

  private tzChangeSubject: Subject<void> = new Subject();
  /** Emits a notification every time the Intervention Fixing TZ format changes  */
  public $tzChange: Observable<void> = this.tzChangeSubject.asObservable();

  constructor(
    profileService: ProfilePromiseActionService,
    // It is not possible to inject against an interface
    @Optional() private _ccyLookupService: CcyLookupService,
    @Optional() private _ccyPairLookupService: CcyPairLookupService,
    @Optional() private _clientLookup: ClientLookupService,
    @Optional() private _accountLookup: AccountLookupService,
    @Optional() private _salesGroupLookup: SalesGroupLookupService,
    @Optional() private _traderGroupLookup: TraderGroupLookupService,
    @Optional() private _userLookup: UserLookupService,
    @Optional() private _translate: TranslateService,
    @Optional() private _sysConfigService: SystemConfigPromiseService,
    @Optional() private _dealtPricingService: DealtPricingService,
    @Optional() @Inject(forwardRef(() => BfxConfigService)) _configService,
    private timezoneLookupService: TimezonePromiseLookupService,
  ) {
    super(profileService);
    this.diStatusHelper = new DIStatusHelper(); // no order data needed
    this.diReasonHelper = new DIReasonHelper(_translate, _ccyLookupService); // no order data needed
    this.transactionHelper = new OrderTransactionHelper(
      _translate,
      _ccyLookupService,
      _ccyPairLookupService,
      _sysConfigService,
    ); // order data needed
    this.myStatusIconHelper = new StatusIconsHelper(
      _userLookup,
      _sysConfigService,
      _translate,
      _dealtPricingService,
      null, // No TSL support
      _traderGroupLookup,
    ); // order data needed
    this.userNameHelper = new UserNameHelper(_userLookup, _sysConfigService); // no order data needed

    _configService.getUIConfig().then((config) => {
      this.enabledOBO = config && config.general ? config.general.oboEnabled : false;
    });

    this.initializePermissions();
    this.initialiseTimezone();
  }

  private initializePermissions(): Promise<boolean> {
    return new Promise<boolean>((res, rej) => {
      if (this.permissionsInitialized === true) {
        res(true);
      } else {
        Promise.all([this._sysConfigService.areBenchmarksEnabled()])
          .then(
            ([benchmarksEnabled]) => {
              this.benchmarksAreEnabled = benchmarksEnabled;
              this.permissionsInitialized = true;
              res(true);
            },
            (rejection) => {
              logger.out.error("ERROR:rejection InterventionTableDefinitionFactory:", rejection);
              this.permissionsInitialized = false;
              res(false);
            },
          )
          .catch((reason) => {
            logger.out.error("ERROR:reason InterventionTableDefinitionFactory: ", reason);
            this.permissionsInitialized = false;
            res(false);
          });
      }
    });
  }
  private initialiseTimezone() {
    const key = ProfileSettingKeys.userProfileSettingsBmarkSummaryPreferences;
    // use BenchmarkSummary Preferences too
    this.profileService.getProfileConfig(key).then((profileValue) => {
      this.initUserSettings(profileValue);
    });

    this.settingSubscription = this.profileService
      .getProfileConfigUpdates(key)
      .subscribe((profileValue) => {
        this.initUserSettings(profileValue);
      });
  }

  /**
   * Process a change to the the global Fixing TZ display setting. Emit an observable event once
   * all processing is complete
   * @param profileValue
   */
  private initUserSettings(profileValue: string) {
    if (typeof profileValue == "string") {
      const savedSettings: any = JSON.parse(profileValue);
      const tzId = savedSettings ? savedSettings.fixingTimeTz : "";
      this.timezoneLookupService.getTimezone(tzId).then((tz) => {
        this.bmDisplayTZ = tz;
        this.tzChangeSubject.next();
      });
    }
  }

  private initColdDef(): any {
    return {
      suppressMenu: true,
      cellClass: [DEFAULT_CELL_CLASS],
      tooltipComponent: "diTooltip",
    };
  }

  public createDef(): TableDefinition {
    super.createDef();
    let td = new TableDefinition("eventId", "as_sort_eventId");
    let positionCounter = 0;

    /* Selection column */
    let selectionColumn: BfxColDef = this.initColdDef();
    selectionColumn.field = "selection";
    selectionColumn.sortingOrder = [];
    selectionColumn.pinned = "left";
    selectionColumn.lockPosition = true;
    selectionColumn.checkboxSelection = true;
    selectionColumn.headerName = "(0)";
    selectionColumn.headerCheckboxSelection = true;
    selectionColumn.headerCheckboxSelectionFilteredOnly = true;
    selectionColumn.suppressSorting = true;
    selectionColumn.suppressMovable = true;
    selectionColumn.suppressResize = true;
    selectionColumn.suppressMenu = true;
    selectionColumn.omitFromPicker = true;
    selectionColumn.disableInPicker = true;
    selectionColumn.columnFilterType = ColumnFilterType.NONE;
    selectionColumn.valueGetter = function (this: InterventionTableDefinitionFactory, params) {
      if (params != null && params.data instanceof CubiqTS.InterventionEventImpl) {
        return this.myStatusIconHelper.getInterventionIcons(params.data);
      }
      return "";
    }.bind(this);
    selectionColumn.cellRenderer = function (params) {
      return params.value != null ? params.value : "";
    };
    selectionColumn.tooltipValueGetter = function (this: null, params) {
      return "";
    };
    // enforced width on fixed column
    selectionColumn.cellClass = ["status-icon-cell"];
    selectionColumn.width = 76;
    selectionColumn.minWidth = this.threeWs;
    selectionColumn.position = positionCounter++;
    selectionColumn.indexType = IndexType.None;
    td.addColumn(selectionColumn);

    /* Timestamp */
    let timestampColumn: BfxColDef = this.initColdDef();
    timestampColumn.headerName = "Timestamp";
    timestampColumn.field = "timestamp";
    timestampColumn.indexType = IndexType.Double;
    timestampColumn.columnFilterType = ColumnFilterType.NONE;
    timestampColumn.valueFormatter = this.formatMomment.bind(this);
    timestampColumn.minWidth = this.oneWs;
    timestampColumn.width = this.eightWs;
    if (!this.restoreFromConfig(timestampColumn)) {
      timestampColumn.position = positionCounter++;
    }
    td.addColumn(timestampColumn);

    /* Event Type */
    let eventTypeColumn: BfxColDef = this.initColdDef();
    eventTypeColumn.headerName = "Event Type";
    eventTypeColumn.field = "eventType";
    eventTypeColumn.indexType = IndexType.String;
    eventTypeColumn.valueGetter = function (params) {
      if (
        params != null &&
        params.data != null &&
        params.data.eventType != null &&
        typeof params.data.eventType === "number" &&
        params.data.eventType >= 0
      ) {
        if (params.data.eventType === CubiqTS.InterventionEventType.LIMIT_ORDER) {
          return "Credit";
        } else if (params.data.eventType === CubiqTS.InterventionEventType.BMARK_ORDER) {
          return params.data.reason === CubiqTS.DIReason.SOFT_CUT_OFF ||
            params.data.reason === CubiqTS.DIReason.CANCEL_DURING_SOFT_CUT_OFF
            ? "Acceptance"
            : "Credit";
        } else if (params.data.eventType === CubiqTS.InterventionEventType.FIXING_RATE) {
          return "Pricing";
        } else if (params.data.eventType === CubiqTS.InterventionEventType.FILL) {
          return "Fill";
        } else if (params.data.eventType === CubiqTS.InterventionEventType.FWD_FILL_PENDING) {
          return "Forward Fill";
        } else if (
          params.data.eventType === CubiqTS.InterventionEventType.PORTFOLIO_MANUAL_PRICING
        ) {
          return "Forward Roll";
        }
      }
      return "-";
    };
    eventTypeColumn.minWidth = this.oneWs;
    eventTypeColumn.width = this.sevenWs;
    eventTypeColumn.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(eventTypeColumn)) {
      eventTypeColumn.position = positionCounter++;
    }
    td.addColumn(eventTypeColumn);

    /* DI Status */
    let diStatusColumn: BfxColDef = this.initColdDef();
    diStatusColumn.headerName = "DI Status";
    diStatusColumn.field = "reason";
    diStatusColumn.indexType = IndexType.String;
    diStatusColumn.columnFilterType = ColumnFilterType.COMPOSITE_ENUM;
    diStatusColumn.columnFilterData = DIFilterReason;
    diStatusColumn.valueGetter = function (params) {
      return this.diStatusHelper.getStatus(params.data, "reason");
    }.bind(this);
    diStatusColumn.minWidth = this.oneWs;
    diStatusColumn.width = this.eightWs;
    if (!this.restoreFromConfig(diStatusColumn)) {
      diStatusColumn.position = positionCounter++;
    }
    td.addColumn(diStatusColumn);

    /* Client Name */
    let clientNameColumn: BfxColDef = this.initColdDef();
    clientNameColumn.headerName = "Client Name";
    clientNameColumn.field = "clientName";
    clientNameColumn.indexType = IndexType.String;
    clientNameColumn.columnFilterType = ColumnFilterType.CLIENT;
    clientNameColumn.valueGetter = (params) => {
      if (params?.data?.orderData instanceof CubiqTS.OrderDataImpl) {
        let order = params.data["orderData"];
        if (this._clientLookup && order && typeof order["clientCode"] === "string") {
          let client: CubiqTS.ClientImpl = this._clientLookup.getClient(order["clientCode"]);
          if (client) {
            return client.clientName;
          }
        }
      } else if (params?.data?.fwdRollOrderData?.clientCd) {
        // ClientCd is inside a fwdRollOrderData for Fwd Roll intervention events
        let client: CubiqTS.ClientImpl = this._clientLookup.getClient(
          params.data.fwdRollOrderData.clientCd,
        );
        if (client) {
          return client.clientName;
        }
      }
      return "-";
    };
    clientNameColumn.getFilterValue = (params) => {
      if (params?.orderData instanceof CubiqTS.OrderDataImpl) {
        let order = params.orderData;
        if (this._clientLookup && order && typeof order["clientCode"] === "string") {
          let client: CubiqTS.ClientImpl = this._clientLookup.getClient(order["clientCode"]);
          if (client) {
            return client.clientName;
          }
        }
      } else if (params?.fwdRollOrderData?.clientCd) {
        // ClientCd is inside a fwdRollOrderData for Fwd Roll intervention events
        let client: CubiqTS.ClientImpl = this._clientLookup.getClient(
          params.fwdRollOrderData.clientCd,
        );
        if (client) {
          return client.clientName;
        }
      }
      return "-";
    };
    clientNameColumn.minWidth = this.oneWs;
    clientNameColumn.width = this.tenWs;
    if (!this.restoreFromConfig(clientNameColumn)) {
      clientNameColumn.position = positionCounter++;
    }
    td.addColumn(clientNameColumn);

    /* Client ID */
    let clientIdColumn: BfxColDef = this.initColdDef();
    clientIdColumn.headerName = "Client ID";
    clientIdColumn.field = "clientCode";
    clientIdColumn.indexType = IndexType.String;
    clientIdColumn.columnFilterType = ColumnFilterType.CLIENTID;
    clientIdColumn.valueGetter = function (params) {
      if (params?.data?.orderData instanceof CubiqTS.OrderDataImpl) {
        let order = params.data["orderData"];
        if (this._clientLookup && order && typeof order["clientCode"] === "string") {
          let client: CubiqTS.ClientImpl = this._clientLookup.getClient(order["clientCode"]);
          if (client) {
            return client.clientId;
          }
        }
      } else if (params?.data?.fwdRollOrderData?.clientCd) {
        // ClientCd is inside a fwdRollOrderData for Fwd Roll intervention events
        return params.data.fwdRollOrderData.clientCd;
      }
      return "-";
    }.bind(this);
    clientIdColumn.minWidth = this.oneWs;
    clientIdColumn.width = this.tenWs;
    if (!this.restoreFromConfig(clientIdColumn)) {
      clientIdColumn.hide = true;
      clientIdColumn.position = positionCounter++;
    }
    td.addColumn(clientIdColumn);

    /*  Instrument */
    let instrumentColumn: BfxColDef = this.initColdDef();
    instrumentColumn.headerName = "Instrument";
    instrumentColumn.field = COLUMN_INTER_INSTRUMENT;
    instrumentColumn.indexType = IndexType.String;
    instrumentColumn.columnFilterType = ColumnFilterType.CURRENCY_PAIR;
    instrumentColumn.valueGetter = function (params) {
      if (params?.data instanceof CubiqTS.InterventionEventImpl) {
        if (
          params.data.orderData instanceof CubiqTS.OrderDataImpl &&
          params.data.orderData.isInvertedOrderFg
        ) {
          return params.data.nonBaseCode + "/" + params.data.baseCode;
        } else if (params.data.fwdRollOrderData != null) {
          // For FWD Roll, instrument depends on number of orders and their associated pairs
          const fwdRollData = params.data.fwdRollOrderData;
          if (fwdRollData.orders.length > 1) {
            let pair: string = null;
            for (let order of fwdRollData.orders) {
              const curPair: string = order.isInvertedOrderFg
                ? order.nonBaseCode + "/" + order.baseCode
                : order.baseCode + "/" + order.nonBaseCode;
              if (null == pair) {
                pair = curPair;
              } else if (pair !== curPair) {
                pair = "-";
                break;
              }
            }
            return pair;
          } else {
            const orderData = fwdRollData.orders[0];
            return orderData.isInvertedOrderFg
              ? orderData.nonBaseCode + "/" + orderData.baseCode
              : orderData.baseCode + "/" + orderData.nonBaseCode;
          }
        } else {
          // fixing rate event
          return params.data.baseCode + "/" + params.data.nonBaseCode;
        }
      }
      return "-";
    };
    instrumentColumn.alias = [COLUMN_INSTRUMENT, COLUMN_TRADE_INSTRUMENT];
    instrumentColumn.minWidth = this.oneWs;
    instrumentColumn.width = this.sixWs;
    instrumentColumn.maxWidth = this.sevenWs;
    if (!this.restoreFromConfig(instrumentColumn)) {
      instrumentColumn.position = positionCounter++;
    }
    td.addColumn(instrumentColumn);

    // // Product
    let productColumn: BfxColDef = this.initColdDef();
    productColumn.headerName = "Product";
    productColumn.field = COLUMN_INTER_ORDER_PRODUCT;
    productColumn.indexType = IndexType.String;
    productColumn.columnFilterType = ColumnFilterType.ENUM;
    productColumn.columnFilterData = CubiqTS.Product;
    productColumn.valueGetter = (params) => {
      if (params?.data?.orderData instanceof CubiqTS.OrderDataImpl) {
        let status: number = -1;

        if (
          typeof params.data.orderData[COLUMN_INTER_ORDER_PRODUCT] === "number" &&
          params.data.orderData[COLUMN_INTER_ORDER_PRODUCT] >= 0
        ) {
          status = params.data.orderData[COLUMN_INTER_ORDER_PRODUCT];
        }
        if (status === CubiqTS.Product.SPT) {
          return "SPT";
        } else if (status === CubiqTS.Product.FWD) {
          return "FWD";
        } else if (status === CubiqTS.Product.NDF) {
          return "NDF";
        }
      } else if (params.data.fwdRollOrderData?.orders?.length > 0) {
        // For FWD Roll, instrument depnends on Netting mode
        const fwdRollData = params.data.fwdRollOrderData as CubiqTS.FwdRollOrderData;
        if (fwdRollData.orders.length > 1) {
          return "Portfolio";
        }
        return this.transactionHelper.getBenchmarkProductLabel(fwdRollData.orders[0].product);
      }
      return "-";
    };
    productColumn.alias = [COLUMN_ORDER_PRODUCT, COLUMN_TRADE_ORDER_PRODUCT];
    productColumn.minWidth = this.oneWs;
    productColumn.width = this.fourWs;
    if (!this.restoreFromConfig(productColumn)) {
      productColumn.position = positionCounter++;
    }
    td.addColumn(productColumn);

    /* Transaction */
    let transactionColumn: BfxColDef = this.initColdDef();
    transactionColumn.headerName = "Transaction";
    transactionColumn.field = "transaction";
    transactionColumn.indexType = IndexType.String;
    transactionColumn.columnFilterType = ColumnFilterType.NONE;
    transactionColumn.columnFilterData = null;
    transactionColumn.valueGetter = (params) =>
      this.transactionHelper.retrieveData(params.data, "transaction");

    transactionColumn.minWidth = this.oneWs;
    transactionColumn.width = this.sixteenWs;

    if (!this.restoreFromConfig(transactionColumn)) {
      transactionColumn.position = positionCounter++;
    }
    td.addColumn(transactionColumn);

    /* DI reason */
    let diReasonColumn: BfxColDef = this.initColdDef();
    diReasonColumn.headerName = "DI Reason";
    diReasonColumn.field = "errors";
    diReasonColumn.indexType = IndexType.String;
    diReasonColumn.columnFilterType = ColumnFilterType.NONE;
    diReasonColumn.columnFilterData = null;
    diReasonColumn.valueGetter = (params) => {
      return this.diReasonHelper.retrieveData(params.data, "errors");
    };
    diReasonColumn.tooltipValueGetter = (params) => {
      return this.diReasonHelper.retrieveTooltip(params.data);
    };
    diReasonColumn.minWidth = this.oneWs;
    diReasonColumn.width = this.eightWs;
    // max length of reason can be large
    // diReasonColumn.maxWidth = this.twenty;
    diReasonColumn.tooltipField = diReasonColumn.field;
    if (!this.restoreFromConfig(diReasonColumn)) {
      diReasonColumn.position = positionCounter++;
    }
    td.addColumn(diReasonColumn);

    /* Locked by */
    let lockedByColumn: BfxColDef = this.initColdDef();
    lockedByColumn.headerName = "Locked By";
    lockedByColumn.field = "lockUserId";
    lockedByColumn.indexType = IndexType.String;
    lockedByColumn.columnFilterType = ColumnFilterType.NONE;
    lockedByColumn.valueGetter = function (params) {
      if (
        params != null &&
        params.data instanceof CubiqTS.InterventionEventImpl &&
        params.data.hasOwnProperty("lockUserId")
      ) {
        if (params.data.orderData instanceof CubiqTS.OrderDataImpl) {
          let order: CubiqTS.OrderDataImpl = params.data.orderData;
          if (
            order.lockType == CubiqTS.LockType.MODIFY_LOCK &&
            MODIFY_LOCK_IGNORE_STATES.includes(order.orderState)
          ) {
            return "-";
          }
        }

        return this.userNameHelper.retrieveData(params.data, "lockUserId");
      }
      return "";
    }.bind(this);
    lockedByColumn.minWidth = this.oneWs;
    lockedByColumn.width = this.sixWs;
    // lockedByColumn.maxWidth = this.twentyWs;
    if (!this.restoreFromConfig(lockedByColumn)) {
      lockedByColumn.position = positionCounter++;
    }
    td.addColumn(lockedByColumn);

    /*  Trader Group  */
    let traderGroupColumn: BfxColDef = this.initColdDef();
    traderGroupColumn.headerName = "Trader Group";
    traderGroupColumn.field = COLUMN_TRADE_DESK_ID;
    traderGroupColumn.indexType = IndexType.String;
    traderGroupColumn.columnFilterType = ColumnFilterType.TRADERGROUP;
    traderGroupColumn.showTooltip = true;
    traderGroupColumn.valueGetter = (params) => {
      const event: CubiqTS.InterventionEvent = params?.data;
      if (event) {
        const fixingRateRequirement = event.fixingRateRequirement;
        if (fixingRateRequirement) {
          let traderGroupIds: CubiqTS.Long[] = fixingRateRequirement.traderGroupIds;
          if (Array.isArray(traderGroupIds)) {
            let traderGroupCodes: string[] = traderGroupIds
              .map((tgId: CubiqTS.Long) => this._traderGroupLookup.getTraderGroup(tgId))
              .filter((a) => a != null)
              .map((traderGroup: CubiqTS.TradingGroupImpl) => traderGroup.groupCode)
              .filter((a) => a != null);
            traderGroupCodes = traderGroupCodes.sort((a, b) => a.localeCompare(b));
            return traderGroupCodes.length > 0 ? traderGroupCodes.join("; ") : "-";
          }
        } else if (event.fwdRollOrderData?.orders) {
          const orders = event.fwdRollOrderData?.orders;
          const groupIds: CubiqTS.Long[] = [];
          const groupMap = {}; // For storing array membership
          orders?.forEach((order) => {
            order.ownerGroupIds?.forEach((groupId) => {
              const idKey = groupId.toString();
              if (!groupMap[idKey]) {
                groupMap[idKey] = true;
                groupIds.push(groupId);
              }
            });
          });
          if (groupIds.length == 1) {
            const traderGroup = this._traderGroupLookup.getTraderGroup(groupIds[0]);
            return traderGroup?.groupCode ?? "-";
          } else {
            return `${groupIds.length} groups`;
          }
        } else {
          const traderGroup = this._traderGroupLookup.getTraderGroup(
            params.data[COLUMN_TRADE_DESK_ID],
          );
          return traderGroup?.groupCode ?? "-";
        }
      }
      return "-";
    };
    traderGroupColumn.alias = [COLUMN_DESK_ID, COLUMN_TRADE_DESK_ID];
    traderGroupColumn.minWidth = this.oneWs;
    traderGroupColumn.width = this.elevenWs;
    // traderGroupColumn.maxWidth = this.twentyWs;
    if (!this.restoreFromConfig(traderGroupColumn)) {
      //traderGroupColumn.hide = true;
      traderGroupColumn.position = positionCounter++;
    }
    td.addColumn(traderGroupColumn);

    /*  Sales Group */
    let salesGroupColumn: BfxColDef = this.initColdDef();
    salesGroupColumn.headerName = "Sales Group";
    salesGroupColumn.field = "salesGroupId";
    salesGroupColumn.indexType = IndexType.String;
    salesGroupColumn.columnFilterType = ColumnFilterType.SALESGROUP;
    salesGroupColumn.valueGetter = function (params) {
      if (
        params != null &&
        params.data instanceof CubiqTS.InterventionEventImpl &&
        params.data.hasOwnProperty("salesGroupId")
      ) {
        let sg = this._salesGroupLookup.getSalesGroup(params.data.salesGroupId);
        return sg instanceof CubiqTS.SalesGroupImpl ? sg.groupCode : "-";
      }
      return "-";
    }.bind(this);
    salesGroupColumn.minWidth = this.oneWs;
    salesGroupColumn.width = this.sevenWs;
    salesGroupColumn.maxWidth = this.twentyWs;
    if (!this.restoreFromConfig(salesGroupColumn)) {
      salesGroupColumn.position = positionCounter++;
    }
    td.addColumn(salesGroupColumn);

    /* Order ID */
    let orderIdColumn: BfxColDef = this.initColdDef();
    orderIdColumn.headerName = "Order ID";
    orderIdColumn.field = "orderData.orderRef";
    orderIdColumn.valueGetter = (params) => {
      const event: CubiqTS.InterventionEvent = params?.data;
      if (event) {
        if (event.orderData instanceof CubiqTS.OrderDataImpl) {
          return event.orderData?.orderRef ?? "-";
        } else if (event.fwdRollOrderData?.orders?.length > 0) {
          const orders = event.fwdRollOrderData.orders;
          if (orders.length == 1) {
            return orders[0]?.orderRef ?? "-";
          }
          return `${orders.length} Orders`;
        }
      }
      return "-";
    };
    // orderIdColumn.valueFormatter = BfxConfigIDRefHelper.interventionOrderRefValueFormatter;
    // orderIdColumn.getQuickFilterText = BfxConfigIDRefHelper.interventionOrderRefValueFormatter;
    orderIdColumn.minWidth = this.oneWs;
    orderIdColumn.width = this.sixWs;
    orderIdColumn.maxWidth = this.twelveWs;
    orderIdColumn.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(orderIdColumn)) {
      orderIdColumn.hide = true;
      orderIdColumn.position = positionCounter++;
    }
    td.addColumn(orderIdColumn);

    /* Cl Order ID */
    let clOrderIdColumn: BfxColDef = this.initColdDef();
    clOrderIdColumn.headerName = "ClOrderId";
    clOrderIdColumn.field = "orderData.clOrdID";
    clOrderIdColumn.indexType = IndexType.String;
    clOrderIdColumn.valueGetter = (params) => {
      const event: CubiqTS.InterventionEvent = params?.data;
      if (event) {
        if (event.orderData instanceof CubiqTS.OrderDataImpl) {
          return event.orderData?.clOrdID ?? "-";
        } else if (event.fwdRollOrderData?.orders?.length > 0) {
          const orders = event.fwdRollOrderData.orders;
          if (orders.length == 1) {
            return orders[0]?.clOrdID ?? "-";
          }
          return `${orders.length} Orders`;
        }
      }
      return "-";
    };
    clOrderIdColumn.minWidth = this.oneWs;
    clOrderIdColumn.width = this.sixWs;
    // max length of clOrdID can be large
    // clOrderIdColumn.maxWidth = this.twelveWs;
    clOrderIdColumn.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(clOrderIdColumn)) {
      clOrderIdColumn.hide = true;
      clOrderIdColumn.position = positionCounter++;
    }
    td.addColumn(clOrderIdColumn);

    /* Channel */
    let channelColumn: BfxColDef = this.initColdDef();
    channelColumn.headerName = "Channel";
    channelColumn.field = "channelCd";
    channelColumn.indexType = IndexType.String;
    channelColumn.columnFilterType = ColumnFilterType.CHANNEL;
    channelColumn.valueGetter = (params) => {
      const event: CubiqTS.InterventionEvent = params?.data;
      let channelCd: string;
      if (event) {
        channelCd = event.channelCd;
        if (!channelCd && event.orderData) {
          channelCd = event.orderData.channelCd;
        } else if (event.fwdRollOrderData?.orders?.length > 0) {
          // Only assign a channelCd if they're all equal
          const orders = event.fwdRollOrderData.orders;
          channelCd = orders[0].channelCd;
          for (let i = 1; i < orders.length; i++) {
            if (orders[i].channelCd !== channelCd) {
              channelCd = null;
            }
          }
        }
      }
      return channelCd ?? "-";
    };
    channelColumn.valueFormatter = (params) => {
      let value: string = params?.value;
      let displayName: string;
      if (value && value != "-") {
        displayName = this._clientLookup.getTradingChannelProfileDisplayName(value);
      }
      return displayName ? displayName : "-";
    };
    channelColumn.minWidth = this.oneWs;
    channelColumn.width = this.fiveWs;
    channelColumn.maxWidth = this.fourteenWs;
    if (!this.restoreFromConfig(channelColumn)) {
      channelColumn.position = positionCounter++;
    }
    td.addColumn(channelColumn);

    /* Fixing Code */
    if (this.benchmarksAreEnabled) {
      let fixingCodeColumn: BfxColDef = this.initColdDef();

      fixingCodeColumn.headerName = "Fixing";
      fixingCodeColumn.field = COLUMN_ORDER_FIXING_SOURCE_CODE_DATA;
      fixingCodeColumn.alias = [COLUMN_ORDER_FIXING_SOURCE_CODE_DATA];
      fixingCodeColumn.minWidth = this.oneWs;
      fixingCodeColumn.width = this.sixWs;
      fixingCodeColumn.omitFromPicker = false;
      fixingCodeColumn.columnFilterType = ColumnFilterType.BM_FIXCODE;
      fixingCodeColumn.valueGetter = (params) => this.createFixingValue(params.data);

      fixingCodeColumn.valueFormatter = (params: ValueFormatterParams) => {
        const value: FixingValue = params.value;
        if (value.fixingTime > 0) {
          return OrderTransactionHelper.getBMFixingDescription(
            value.fixingMoment,
            value.code,
            this.bmDisplayTZ,
          );
        }
        return "-";
      };
      fixingCodeColumn.comparator = (value1: FixingValue, value2: FixingValue) => {
        let result = value1.fixingTime - value2.fixingTime;
        if (result == 0) {
          return value1.code.localeCompare(value2.code);
        }
        return result;
      };

      if (!this.restoreFromConfig(fixingCodeColumn)) {
        fixingCodeColumn.position = positionCounter++;
      }
      td.addColumn(fixingCodeColumn);
    }

    if (this.enabledOBO) {
      // Reset here for new columns
      positionCounter = 50;

      // OBO Client
      let oboClient: BfxColDef = this.initColdDef();
      oboClient.headerName = "OBO Client";
      oboClient.field = "oboClient";
      oboClient.minWidth = this.oneWs;
      oboClient.width = this.sevenWs;
      oboClient.columnFilterType = ColumnFilterType.NONE;

      oboClient.valueGetter = (params) => {
        const event: CubiqTS.InterventionEvent = params?.data;
        let oboValue = null;
        if (event) {
          if (event.orderData instanceof CubiqTS.OrderDataImpl) {
            oboValue = event.orderData?.oboClient;
          } else if (event.fwdRollOrderData?.orders?.length > 0) {
            const orders = event.fwdRollOrderData.orders;
            const oboMap = new Map();
            const oboValues = [];

            for (let i = 0; i < orders.length; i++) {
              oboValue = orders[i].oboClient ?? "-";
              if (!oboMap[oboValue]) {
                oboMap[oboValue] = true;
                oboValues.push(oboValue);
              }
            }
            if (oboValues.length == 1) {
              return oboValues[0];
            }
            return `${oboValues.length} OBO Clients`;
          }
        }
        return oboValue ?? "-";
      };

      if (!this.restoreFromConfig(oboClient)) {
        oboClient.hide = true;
        oboClient.position = positionCounter++;
      }

      td.addColumn(oboClient);

      // OBO Account
      let oboAccount: BfxColDef = this.initColdDef();
      oboAccount.headerName = "OBO Account";
      oboAccount.field = "oboAccount";
      oboAccount.minWidth = this.oneWs;
      oboAccount.width = this.sevenWs;
      oboAccount.columnFilterType = ColumnFilterType.NONE;

      oboAccount.valueGetter = (params) => {
        const event: CubiqTS.InterventionEvent = params?.data;
        let oboValue = null;
        if (event) {
          if (event.orderData instanceof CubiqTS.OrderDataImpl) {
            oboValue = event.orderData?.oboAccount;
          } else if (event.fwdRollOrderData?.orders?.length > 0) {
            const orders = event.fwdRollOrderData.orders;
            const oboMap = new Map();
            const oboValues = [];

            for (let i = 0; i < orders.length; i++) {
              oboValue = orders[i].oboAccount ?? "-";
              if (!oboMap[oboValue]) {
                oboMap[oboValue] = true;
                oboValues.push(oboValue);
              }
            }
            if (oboValues.length == 1) {
              return oboValues[0];
            }
            return `${oboValues.length} OBO Accounts`;
          }
        }
        return oboValue ?? "-";
      };
      if (!this.restoreFromConfig(oboAccount)) {
        oboAccount.hide = true;
        oboAccount.position = positionCounter++;
      }

      td.addColumn(oboAccount);
    }

    /* Portfolio Netting ID */
    const nettingIdColumn: BfxColDef = this.initColdDef();
    nettingIdColumn.headerName = "Portfolio";
    nettingIdColumn.field = "orderData.fwdRollOrderData.portfolioId";
    nettingIdColumn.valueGetter = (params) => {
      const event: CubiqTS.InterventionEvent = params?.data;
      return event?.fwdRollOrderData?.portfolioId?.toString() ?? "-";
    };
    nettingIdColumn.minWidth = this.oneWs;
    nettingIdColumn.width = this.sixWs;
    nettingIdColumn.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(nettingIdColumn)) {
      nettingIdColumn.hide = true;
      nettingIdColumn.position = positionCounter++;
    }
    td.addColumn(nettingIdColumn);

    return td;
  }

  public getBlotterType(): BlotterType {
    return BlotterType.INTERVENTION;
  }

  public getAssetClass(): AssetClass {
    return AssetClass.FIX;
  }

  public getVersionIdentifier(): string {
    return INTERVENTION_TABLE_DEFINITION_VER;
  }

  /**
   * Create a distinct fixing time value from an InterventionEvent, used in sorting and display - fixing times can be taken from orders,
   * fwdRoll or Prixing events
   * @param event
   * @returns
   */
  private createFixingValue(event: CubiqTS.InterventionEventImpl): FixingValue {
    const retValue: FixingValue = { fixingMoment: null, fixingTime: 0, code: "", order: null };
    if (event?.fixingRateRequirement) {
      retValue.fixingMoment = event.fixingRateRequirement.fixingTime;
      retValue.fixingTime = event.fixingRateRequirement.fixingTime?.valueOf() ?? 0;
    } else {
      let order: CubiqTS.OrderData;
      if (event?.orderData?.orderType) {
        order = event.orderData;
      } else if (event?.fwdRollOrderData?.orders?.length > 0) {
        order = event?.fwdRollOrderData?.orders[0];
      }
      retValue.order = order;
      if (
        order &&
        (order.orderType === CubiqTS.OrderType.BENCHMARK ||
          order.orderType === CubiqTS.OrderType.BMALGO)
      ) {
        retValue.fixingMoment = order.fixingDate;
        retValue.fixingTime = order.fixingDate.valueOf();
        retValue.code = order.fixingSourceCd;
      }
    }
    return retValue;
  }
}
